import { useState } from "react";
import { useDemo } from "../zustand/DemoZustand";
import { Abschnitt, Marke } from "../bausteine/Grundelemente";
import Aufgabenbrett from "../bausteine/Aufgabenboard";
import Kalenderblatt from "../bausteine/Kalenderblatt";
import Terminkarte from "../bausteine/Terminkarte";
import Aufgabenkarte from "../bausteine/Aufgabenkarte";
import {
  Dokument,
  Kalender,
  Liste,
  Ordner,
  PfeilRechts,
  Schloss,
  Sprechblase,
  Warnung,
} from "../bausteine/Symbole";
import { STANDBILDER } from "./Standbilder";
import {
  gehoertZuMir,
  kalendereintraege,
  kanaluebersicht,
  nameVon,
  offenePunkte,
  topZuDatei,
} from "../hilfen/auswertung";
import { useChat } from "../zustand/ChatZustand";
import { gleicherTag, heute, kurzesDatum, uhrzeit, wochentag } from "../hilfen/datum";

/*
  Das Dashboard: vier Kacheln, zwei davon über die ganze Breite.

    Nächste Sitzungen und Termine   |   Chats
    Aufgaben — das Brett, bedienbar
    Vorlagen

  Ursprünglich standen alle vier als 2×2 nebeneinander und passten zugleich
  auf den Bildschirm. Das ist aufgegeben, seit die Aufgaben hier nicht mehr
  nur einzusehen, sondern zu ORDNEN sind: Ein Brett braucht seine Spalten
  nebeneinander, und in einer halbbreiten Kachel brechen deutsche
  Aufgabentitel auf vier Zeilen. Die Vorlagen rücken dafür darunter,
  ebenfalls über die ganze Breite.

  Was man dafür aufgibt: den Blick, der alles auf einmal erfasst. Was man
  gewinnt: eine Startseite, auf der man arbeitet statt nur nachzusehen.

  Jede Kachel führt über „Alle“ in den vollen Bereich. Die Leitfrage des
  Lastenhefts — „Was muss ich bis zur nächsten Sitzung tun?“ — steht
  bewusst in der ersten Kachel.
*/

export default function Dashboard({ oeffne }) {
  /*
    Die Karte einer Aufgabe geht hier auf, nicht erst im Aufgabenbereich.
    Wer auf der Startseite ordnet, will nicht für jede Kleinigkeit die
    Ansicht wechseln.
  */
  const [karte, setzeKarte] = useState(null);
  const [terminKarte, setzeTerminKarte] = useState(null);

  return (
    <>
      <div className="dashboard">
        <KalenderKachel oeffne={oeffne} aufTermin={setzeTerminKarte} />
        <ChatKachel oeffne={oeffne} />
        <AufgabenKachel oeffne={oeffne} aufKarte={setzeKarte} />
        <VorlagenKachel oeffne={oeffne} />
      </div>

      {karte && (
        <Aufgabenkarte
          aufgabeId={karte}
          aufSchliessen={() => setzeKarte(null)}
          oeffne={oeffne}
        />
      )}

      {terminKarte && (
        <Terminkarte
          terminId={terminKarte}
          aufSchliessen={() => setzeTerminKarte(null)}
          oeffne={oeffne}
        />
      )}

      <Abschnitt
        titel="Weitere Bereiche"
        hinweis="Zeigen den geplanten Funktionsumfang. In dieser Demo Standbilder, nicht bedienbar."
      >
        {STANDBILDER.map((b) => (
          <button
            key={b.schluessel}
            className="bereich"
            onClick={() => oeffne("standbild", { welches: b.schluessel })}
          >
            <span style={{ flex: 1 }}>
              <span style={{ fontWeight: 600 }}>{b.titel}</span>
              <span className="klein leise" style={{ display: "block", marginTop: "0.25rem" }}>
                {b.kurz}
              </span>
              <span className="top__marken">
                <Marke symbol={Warnung}>Standbild</Marke>
              </span>
            </span>
            <span className="punkt__pfeil">
              <PfeilRechts groesse={18} />
            </span>
          </button>
        ))}
      </Abschnitt>
    </>
  );
}

function Kachel({ titel, symbol: Symbol, mehr, aufMehr, breit = false, children }) {
  return (
    <section className={`kachel ${breit ? "kachel--breit" : ""}`}>
      <div className="kachel__kopf">
        <h2 className="kachel__titel">
          {Symbol && <Symbol groesse={18} />}
          {titel}
        </h2>
        {mehr && (
          <button className="kachel__mehr" onClick={aufMehr}>
            {mehr}
          </button>
        )}
      </div>
      <div className="kachel__inhalt">{children}</div>
    </section>
  );
}

/* ---------- 1. Kalender ---------- */

/*
  Oben links steht ein echtes Monatsblatt, kein Ausschnitt aus zwei Zeilen.

  Der Sitzungszyklus ist der Takt dieser Zielgruppe — wer wissen will, was im
  September ansteht, muss es sehen können. Darunter die kommenden Sitzungen
  als schlichte Liste: Name, Datum, Weg zu den Unterlagen.

  Die Leitfrage des Steckbriefs — „Was muss ich bis zur nächsten Sitzung
  tun?“ — ist damit nicht verschwunden, sondern an die Sitzung gerückt, um
  die es geht: Die nächste trägt eine Marke, solange etwas offen ist. Das ist
  näher an der Sache als eine Liste daneben.
*/
function KalenderKachel({ oeffne, aufTermin }) {
  const { stammdaten, zustand, ich } = useDemo();
  const [tag, setzeTag] = useState(null);
  const [eintragen, setzeEintragen] = useState(false);

  /* Sitzungen der Verwaltung und eigene Termine in einem Blatt. */
  const plan = kalendereintraege(stammdaten, zustand);
  const offen = offenePunkte(zustand, stammdaten, ich.id);
  const jetzt = heute();

  /*
    Vier Einträge — zwei Sitzungen mit ihrer Vorbereitung. Mehr passt unter
    das Monatsblatt nicht, ohne dass die Kachel zur Liste wird; der Rest
    steht im Blatt darüber und hinter „Zur Sitzung“.
  */
  const kommende = plan.filter((e) => e.beginn >= jetzt && e.art !== "termin").slice(0, 4);
  const amTag = tag ? plan.filter((e) => gleicherTag(e.beginn, tag)) : [];

  return (
    <Kachel
      titel="Kalender"
      symbol={Kalender}
      mehr="Zur Sitzung"
      aufMehr={() => oeffne("sitzung", {})}
    >
      <Kalenderblatt eintraege={plan} aufTag={setzeTag} gewaehlterTag={tag} />

      {/*
        Ein angetippter Tag zeigt, was an ihm liegt. Ohne das wäre das Blatt
        eine Anzeige und keine Bedienung — man sähe den Punkt und käme nicht
        heran.
      */}
      {tag && amTag.length > 0 && (
        <div className="kalender__auswahl">
          {amTag.map((e) =>
            e.art === "termin" ? (
              <button key={e.id} className="kalender__eintrag" onClick={() => aufTermin(e.id)}>
                <strong>{uhrzeit(e.beginn)}</strong> · {e.name}
                {e.ort ? ` · ${e.ort}` : ""}
              </button>
            ) : (
              <p key={e.id} className="klein">
                <strong>{uhrzeit(e.beginn)}</strong> · {e.name}
                {e.ort ? ` · ${e.ort}` : ""}
              </p>
            )
          )}
        </div>
      )}

      <p className="kalender__ueberschrift">Kommende Sitzungen</p>

      <div className="terminliste">
        {kommende.length === 0 && (
          <p className="leise klein">Es steht keine Sitzung an.</p>
        )}

        {kommende.map((e) => (
          <Sitzungszeile
            key={e.id}
            eintrag={e}
            /*
              Die Marke gehört an die Ausschusssitzung, nicht an die
              Fraktionssitzung davor: Ungelesene Vorlagen und fehlendes
              Meinungsbild betreffen die Beratung im Ausschuss. An beiden
              Zeilen sähe es nach doppelt so viel Arbeit aus.
            */
            offen={e.istNaechste && e.art === "sitzung" ? offen : null}
            oeffne={oeffne}
          />
        ))}
      </div>

      {eintragen ? (
        <NeuerTermin tag={tag} aufFertig={() => setzeEintragen(false)} />
      ) : (
        <button
          className="brett__plus"
          style={{ marginTop: "var(--luft-3)" }}
          onClick={() => setzeEintragen(true)}
        >
          + Termin eintragen
        </button>
      )}
    </Kachel>
  );
}

/*
  Ein Termin braucht zum Anlegen zwei Angaben: was und wann. Alles Weitere —
  Ort, Eingeladene, Verknüpfungen — steht auf der Karte, die danach aufgeht.
  Ein Formular mit acht Feldern hielte niemanden davon ab, den Termin gar
  nicht erst einzutragen.

  Ist im Blatt ein Tag gewählt, ist er voreingestellt.
*/
function NeuerTermin({ tag, aufFertig }) {
  const { aktionen } = useDemo();
  const [titel, setzeTitel] = useState("");
  const [wann, setzeWann] = useState(() => vorschlag(tag));
  const [fehler, setzeFehler] = useState(null);

  async function anlegen() {
    if (!titel.trim() || !wann) return;
    try {
      await aktionen.fuegeTerminHinzu({ titel: titel.trim(), beginn: wann });
      aufFertig();
    } catch (f) {
      setzeFehler(f.message || "Der Termin ließ sich nicht eintragen.");
    }
  }

  return (
    <div className="brett__neu" style={{ marginTop: "var(--luft-3)" }}>
      <input
        className="brett__feld"
        autoFocus
        placeholder="Was ist das für ein Termin?"
        value={titel}
        onChange={(e) => setzeTitel(e.target.value)}
        onKeyDown={(e) => e.key === "Enter" && anlegen()}
        aria-label="Titel des Termins"
      />
      <input
        type="datetime-local"
        className="brett__feld"
        value={wann}
        onChange={(e) => setzeWann(e.target.value)}
        aria-label="Wann"
      />
      {fehler && (
        <p className="klein" style={{ color: "var(--ampel-nein)" }}>
          {fehler}
        </p>
      )}
      <div className="leiste__knoepfe">
        <button className="knopf" onClick={anlegen} disabled={!titel.trim() || !wann}>
          Eintragen
        </button>
        <button className="knopf knopf--schlicht" onClick={aufFertig}>
          Abbrechen
        </button>
      </div>
    </div>
  );
}

/* Der gewählte Tag um 18 Uhr — sonst morgen um 18 Uhr. */
function vorschlag(tag) {
  const d = tag ? new Date(tag) : new Date(Date.now() + 86400000);
  d.setHours(18, 0, 0, 0);
  const zz = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${zz(d.getMonth() + 1)}-${zz(d.getDate())}T${zz(d.getHours())}:${zz(
    d.getMinutes()
  )}`;
}

/*
  Eine Zeile: Datum, Name, Weg zu den Unterlagen.

  Eine Marke steht nur an der nächsten Sitzung, und nur wenn etwas offen ist.
  Für die späteren Sitzungen hat die Verwaltung noch keine Tagesordnung
  veröffentlicht — dort wäre jede Marke erfunden, und ein Verweis auf
  Unterlagen führte ins Leere. Deshalb steht dort ein Satz statt eines
  Knopfes.
*/
function Sitzungszeile({ eintrag, offen, oeffne }) {
  const eigene = eintrag.art === "fraktionssitzung";
  /*
    Nur die nächste Sitzung hat Unterlagen. Für die späteren hat die
    Verwaltung noch keine Tagesordnung veröffentlicht — das ist keine Lücke
    im Prototyp, sondern die Wirklichkeit.

    Zur Fraktionssitzung gibt es nie eine Tagesordnung der Verwaltung: Sie
    ist kein Gremium im Sinne der Gemeindeordnung. Dort steht deshalb der
    Ort und kein Hinweis, der nach einem Mangel klingt.
  */
  const ohneUnterlagen = !eintrag.istNaechste;

  const fehlt = offen
    ? [
        offen.ungelesen.length > 0 && `${offen.ungelesen.length} Vorlagen ungelesen`,
        offen.ohneMeinung.length > 0 && `${offen.ohneMeinung.length}× Meinungsbild fehlt`,
        offen.teilnahmeOffen && "Rückmeldung fehlt",
      ].filter(Boolean)
    : [];

  const inhalt = (
    <>
      <span className="terminzeile__datum">
        <span className="terminzeile__tag">{wochentag(eintrag.beginn).slice(0, 2)}</span>
        {kurzesDatum(eintrag.beginn)}
      </span>
      <span className="terminzeile__haupt">
        <span className="terminzeile__name">{eintrag.name}</span>
        <span className="terminzeile__zusatz">
          {uhrzeit(eintrag.beginn)}
          {eintrag.abgesagt ? " · abgesagt" : ""}
          {eigene && eintrag.ort ? ` · ${eintrag.ort}` : ""}
          {!eigene && ohneUnterlagen ? " · Tagesordnung noch nicht veröffentlicht" : ""}
        </span>
        {fehlt.length > 0 && (
          <span className="top__marken">
            {fehlt.map((wort) => (
              <Marke key={wort} symbol={Warnung}>
                {wort}
              </Marke>
            ))}
          </span>
        )}
      </span>
    </>
  );

  if (ohneUnterlagen) {
    return <div className="terminzeile terminzeile--still">{inhalt}</div>;
  }

  return (
    <button className="terminzeile" onClick={() => oeffne("sitzung", {})}>
      {inhalt}
      <span className="punkt__pfeil">
        <PfeilRechts groesse={18} />
      </span>
    </button>
  );
}

/* ---------- 2. Chats ---------- */

function ChatKachel({ oeffne }) {
  const { stammdaten, zustand, ich } = useDemo();
  const chat = useChat();
  const kanaele = kanaluebersicht(
    stammdaten,
    { ...zustand, nachrichten: chat.nachrichten },
    ich.id
  );

  return (
    <Kachel
      titel="Chats"
      symbol={Sprechblase}
      mehr="Alle"
      aufMehr={() => oeffne("chats", {})}
    >
      {kanaele.slice(0, 5).map(({ kanal, letzter, ungelesen, stumm }) => (
        <button
          key={kanal.id}
          className="chatzeile"
          onClick={() => oeffne("kanal", { kanalId: kanal.id })}
        >
          <span className="chatzeile__haupt">
            <span className="chatzeile__titel">{kanal.name}</span>
            <span className="chatzeile__vorschau">
              {letzter
                ? `${nameVon(stammdaten, letzter.verfasserId, ich.id)}: ${letzter.text}`
                : "Noch keine Beiträge"}
            </span>
          </span>
          {/* Nur Offenes trägt eine Zahl — ein gelesener Kanal bleibt still. */}
          {ungelesen > 0 && (
            <span className={`chatzeile__anzahl ${stumm ? "chatzeile__anzahl--stumm" : ""}`}>
              {ungelesen}
            </span>
          )}
        </button>
      ))}
    </Kachel>
  );
}

/* ---------- 3. Vorlagen der Verwaltung ---------- */

function VorlagenKachel({ oeffne }) {
  const { stammdaten, zustand } = useDemo();

  /*
    Archivierte Fassungen bleiben erreichbar, aber nicht hier. Die Kachel
    beantwortet die Frage „Was liegt zur nächsten Sitzung vor?“ — und das ist
    immer die geltende Fassung. Der Weg zur alten führt über die Vorlage
    selbst, wo auch dabeisteht, was daran hängt.
  */
  const vorlagen = Object.values(stammdaten.vorlagen).filter((v) => !v.archiviert);

  return (
    <Kachel
      titel="Vorlagen"
      symbol={Ordner}
      mehr="Zur Sitzung"
      aufMehr={() => oeffne("sitzung", {})}
      breit
    >
      <div className="ordnerraster">
        {vorlagen.map((vorlage) => {
          const datei = vorlage.dateiId ? stammdaten.dateien[vorlage.dateiId] : null;
          const gelesen = datei ? zustand.gelesen.includes(datei.id) : false;
          return (
            <button
              key={vorlage.id}
              className="ordner"
              disabled={!datei}
              onClick={() => datei && oeffne("sitzung", { topId: topZuDatei(stammdaten, datei.id)?.id, reiter: "vorlage" })}
            >
              <span className="ordner__symbol">
                {vorlage.nichtoeffentlich ? <Schloss groesse={22} /> : <Dokument groesse={22} />}
              </span>
              <span className="ordner__name">{vorlage.kurztitel}</span>
              <span className="ordner__zusatz">
                {vorlage.aktenzeichen}
                <br />
                {datei
                  ? `${datei.seitenzahl} Seiten · ${gelesen ? "gelesen" : "ungelesen"}`
                  : "Keine Unterlagen"}
              </span>
            </button>
          );
        })}
      </div>
    </Kachel>
  );
}

/* ---------- 4. Aufgaben ---------- */

/*
  Hier steht das Brett selbst, nicht nur eine Liste — und zwar über die
  ganze Breite.

  Der frühere Einwand gegen das Brett auf der Startseite war der Platz: In
  einer halbbreiten Kachel brechen deutsche Aufgabentitel auf vier Zeilen,
  und drei Spalten nebeneinander gehen nicht. Über die volle Breite fällt
  das weg. Dafür rückt die Vorlagen-Kachel darunter, ebenfalls über die
  ganze Breite.

  Gezeigt wird „Dein Board“: was einen selbst angeht. Die Startseite
  beantwortet „Was muss ich bis zur nächsten Sitzung tun?“ — das
  Fraktionsboard steht unter „Alle“.

  Geordnet wird hier genauso wie im Aufgabenbereich: Karte halten und
  ziehen, oder „→ eine Spalte weiter“. Es ist dasselbe Bauteil, kein
  zweites, das später auseinanderliefe.
*/
function AufgabenKachel({ oeffne, aufKarte }) {
  const { zustand, ich } = useDemo();
  const offen = zustand.aufgaben.filter((a) => gehoertZuMir(a, ich.id) && !a.erledigt);

  return (
    <Kachel
      titel="Aufgaben"
      symbol={Liste}
      mehr="Alle"
      aufMehr={() => oeffne("aufgaben", {})}
      breit
    >
      <p className="klein leise" style={{ marginBottom: "var(--luft-3)" }}>
        {offen.length === 0
          ? "Für dich ist nichts offen."
          : `${offen.length} offen.`}{" "}
        Karte antippen öffnet sie; halten und ziehen verschiebt sie.
      </p>
      <Aufgabenbrett brett="meins" aufKarte={aufKarte} />
    </Kachel>
  );
}
